import { type FC, useState } from "react";
import { formatDate } from "../lib/date_utils";
import { CopyButton } from "./copy_button";
import { KeyModeSelector } from "./device_groups/key_mode_selector";
import { updateKeyMode } from "../stores/device_group_store";
import type { DeviceGroup } from "../../../shared/models/device_group";
import type { CertificatePolicy } from "../../../shared/models/certificate_policy";

export const GroupDetailModal: FC<{
  group: DeviceGroup;
  policy?: CertificatePolicy;
  onClose: () => void;
  onPolicyClick?: () => void;
}> = ({ group, policy, onClose, onPolicyClick }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [keyMode, setKeyMode] = useState(group.keyMode);
  const [certificate, setCertificate] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [success, setSuccess] = useState<string | undefined>();

  const handleUpdateKeyMode = async () => {
    setError(undefined);
    setSuccess(undefined);
    if (
      keyMode === "customer-managed" &&
      (certificate.trim() === "" || privateKey.trim() === "")
    ) {
      setError(
        "Certificate and private key are required for customer-managed keys."
      );
      return;
    }
    setIsSubmitting(true);
    try {
      await updateKeyMode(
        group.id,
        keyMode,
        keyMode === "customer-managed" ? certificate : undefined,
        keyMode === "customer-managed" ? privateKey : undefined
      );
      setIsEditing(false);
      setSuccess("Key mode updated successfully.");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update key mode."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const detailRow = (label: string, value: string | React.ReactNode) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 16,
        padding: "10px 0",
        borderBottom: "1px solid var(--vmx-gray-100)",
      }}
    >
      <span
        style={{
          fontSize: "0.8125rem",
          color: "var(--vmx-gray-500)",
          fontWeight: 500,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: "0.8125rem",
          color: "var(--vmx-gray-800)",
          fontWeight: 600,
        }}
      >
        {value}
      </span>
    </div>
  );

  return (
    <div className="vmx-modal" style={{ maxWidth: 480 }}>
      <div className="vmx-modal-header">
        <div className="vmx-modal-title">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span>{group.name}</span>
            {group.isDefault && (
              <span className="vmx-badge vmx-badge-neutral">Default</span>
            )}
          </div>
        </div>
        <button
          className="vmx-btn vmx-btn-ghost vmx-btn-icon"
          onClick={onClose}
        >
          ✕
        </button>
      </div>
      <div className="vmx-modal-body">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            fontFamily: "var(--vmx-font-mono)",
            fontSize: "0.75rem",
            color: "var(--vmx-gray-400)",
            marginBottom: 16,
          }}
        >
          <span>{group.id}</span>
          <CopyButton text={group.id} absolute={false} />
        </div>

        {detailRow(
          "Certificate Policy",
          policy ? (
            <button
              type="button"
              onClick={onPolicyClick}
              style={{
                color: "var(--vmx-accent-dark)",
                fontWeight: 600,
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                fontSize: "0.8125rem",
                textDecoration: "underline",
              }}
            >
              {policy.name}
            </button>
          ) : (
            group.certificatePolicyId
          )
        )}
        {detailRow("Created", formatDate(group.createdAt))}
        {detailRow(
          "Key Mode",
          group.keyMode === "customer-managed"
            ? "Customer-Managed"
            : "Verimatrix-Managed"
        )}

        {!isEditing && (
          <button
            type="button"
            className="vmx-btn vmx-btn-secondary vmx-btn-sm"
            onClick={() => setIsEditing(true)}
            style={{ marginTop: 16 }}
          >
            Change Key Mode
          </button>
        )}

        {success !== undefined && (
          <div
            style={{
              marginTop: 8,
              fontSize: "0.8125rem",
              color: "var(--vmx-success)",
            }}
          >
            {success}
          </div>
        )}

        {isEditing && (
          <div style={{ marginTop: 16 }}>
            <KeyModeSelector
              keyMode={keyMode}
              onKeyModeChange={setKeyMode}
              certificate={certificate}
              onCertificateChange={setCertificate}
              privateKey={privateKey}
              onPrivateKeyChange={setPrivateKey}
            />
            {error !== undefined && (
              <div
                style={{
                  marginTop: 8,
                  fontSize: "0.8125rem",
                  color: "var(--vmx-danger)",
                }}
              >
                {error}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button
                type="button"
                className="vmx-btn vmx-btn-secondary vmx-btn-sm"
                onClick={() => {
                  setIsEditing(false);
                  setError(undefined);
                }}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="vmx-btn vmx-btn-primary vmx-btn-sm"
                onClick={handleUpdateKeyMode}
                disabled={
                  isSubmitting ||
                  (keyMode === "customer-managed" &&
                    (certificate.trim() === "" || privateKey.trim() === ""))
                }
              >
                {isSubmitting ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="vmx-modal-footer">
        <button className="vmx-btn vmx-btn-secondary" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
};
